import { reactive } from 'vue'
import * as _ from 'lodash-es'

import loginService from '../../login/service/loginService.js'
import notificationService from '../../resources/service/notificationService.js'
import resourcesService from '../../resources/service/resourcesService.js'

/*
 * Which resources (calendars) the user has chosen to see, and their order.
 *
 * Was an Angular factory taking `localStorageService` (step 35 of #614). The storage calls keep
 * `angular-local-storage`'s behaviour exactly - the `ls.` prefix and JSON encoding - so a browser
 * that already has a saved selection keeps it. Writing the bare key would silently reset everyone's
 * calendars and their order. `notificationService` in step 32 did the same for its key.
 *
 * The list is `Vue.observable` (step 35b). Now that the resource-selection slide-out is Vue, the
 * change happens outside an Angular digest, and the resources calendar used to react through
 * `services.watch` (`$rootScope.$watch`), which therefore never fired - the calendar kept showing
 * the old resources. A reactive list lets the calendar use an ordinary Vue watcher, as
 * `calendarService` and `alertService` do.
 */
const RESOURCE_SELECTIONS_KEY = 'ls.resourceSelections'

function getSavedResources() {
  try {
    return JSON.parse(localStorage.getItem(RESOURCE_SELECTIONS_KEY))
  } catch {
    // Not JSON, so not something this wrote. Start over rather than failing the resource list.
    return undefined
  }
}

function saveResources(resources) {
  localStorage.setItem(RESOURCE_SELECTIONS_KEY, JSON.stringify(resources))
}

const state = reactive({ allResources: [] })

const resourceSelectionService = {
  state,

  getResources: async () => {
    // get all resources from the database
    const resources = (await resourcesService.update()) || []
    const localResources = getSavedResources()

    // if there is nothing in local storage, just select all resources
    if (!localResources) {
      state.allResources = resources.map(resource => {
        return { ...resource, selected: true }
      })
    } else {
      // put all the ones from local that are also on the server, at the front. Then add the rest to be back. Then uniqBy to dedupe.
      state.allResources = _.uniqBy([..._.intersectionBy(localResources, resources, '_id'), ...resources], '_id')
    }

    // we always want the assigned users from the databas - this is not necessary any more, database is source of truth
    for (const resource of resources) {
      const index = state.allResources.findIndex(res => res.name === resource.name)
      if (index !== -1) {
        state.allResources[index].users = resource.users
      }
    }

    // always notify the current user
    let currentUser = await loginService.currentSession()
    // v6 sessions carry their tokens under `tokens`.
    const payload = currentUser && currentUser.tokens && currentUser.tokens.idToken.payload
    _.each(state.allResources, resource => {
      _.each(resource.users, user => {
        // The stored `userId` is the Cognito sub. Assignments made before the pool switch carry
        // the old pool's sub, so fall back to the email that was stored with them - otherwise an
        // existing notification looks unassigned until someone re-creates it.
        const sameUser =
          payload &&
          (payload['cognito:username'] === user.userId ||
            (payload.email && user.email && payload.email.toLowerCase() === user.email.toLowerCase()))
        if (sameUser) {
          resource.isAssigned = true
          if (!notificationService.notificationsConfigured(resource.name)) {
            notificationService.toggleNotifications(resource.name)
          }
        }
      })
    })

    return state.allResources
  },

  updateSavedResources: () => {
    var filtered = _.filter(state.allResources, ['selected', true])
    resourceSelectionService.setAllResources()
    return filtered
  },

  setAllResources: () => {
    if (state.allResources && state.allResources.length > 0) {
      saveResources(state.allResources)
    }
  },

  // Hide one resource from the calendar without opening the resource selector. Kept here rather
  // than in the calendar so the selection stays in one place: the selector, the calendar's x
  // button and the saved order all write the same list.
  deselectResource: resourceId => {
    const resource = _.find(state.allResources, { _id: resourceId })
    if (resource) {
      resource.selected = false
    }
    return resourceSelectionService.updateSavedResources()
  },

  selectAll: () => {
    _.each(state.allResources, resource => {
      resource.selected = true
    })
    return state.allResources
  },

  selectNone: () => {
    _.each(state.allResources, resource => {
      resource.selected = false
    })
    return state.allResources
  },
}

export default resourceSelectionService
